import React from 'react';

// Pattern for identifying choices, for e.g. (A) in
// a MC question.
const  regExpChoice = /\s*[(][A-Z0-9][)]/gi;

// Pattern for identifying diagram questions.
// We represent diagrams through {http...} substrings in questions
const regExpDiagram = /\{https?:\/\/[^\s}]+\}/g;

// Pattern to capture answer choice labels and answer choices for
// a MC question.
const regExpAnswers = /(\([A-Z0-9]+\))([^(]+)/gi;

/**
 * Utility question text massaging functions
 * used in QuestionSelector and AnswerPage to display correctly formatted
 * questions.
 */
const QuestionUtil = {

  /**
   * Formats question text for display: inserts a newline after the
   * question and before each of the answer choices, and a tab in
   * front of each answer choice.
   */
  formattedQuestion(inputQuestion) {
    let first = true;
    return inputQuestion.replace(
      regExpChoice,
      function(choiceLabel) {
        let prefix = "\n\t";
        if(first) {
          prefix = "\n" + prefix;
        }
        first = false;
        return prefix + choiceLabel.trim();
      }
    );
  },

  /**
   * Breaks up a question into different parts to help
   * render diagram questions correctly with the diagram following
   * the question and then answer choices.
   */
  questionFragments(inputQuestion) {
    const formattedQuestion = QuestionUtil.formattedQuestion(inputQuestion);
    let diagramMatch;
    let diagramMatches = [];
    // Get all matches for some text followed by a "http" link in the question.
    // For e.g., in the question:
    // "Base your answers on the diagram of a food chain below and on your knowledge
    // of science. {http://aristo-data.s3.amazonaws.com/images/NYSEDREGENTS/2005_4th_Grade/01.PNG}
    // If the population of snakes increases, the population of frogs will most likely
    // (A) decrease (B) increase (C) remain the same"
    // there will be one match below for the part: "Base your answers on the diagram of
    // a food chain below and on your knowledge of science.
    // {http://aristo-data.s3.amazonaws.com/images/NYSEDREGENTS/2005_4th_Grade/01.PNG}".
    while ((diagramMatch = regExpDiagram.exec(formattedQuestion))  !== null) {
      diagramMatches.push(diagramMatch);
    }

    let index = 0;
    let fragments = [];
    // Create an array of fragments, each containing a preUrl and a url. In the above e.g., these
    // would respectively be "Base your answers on the diagram of a food chain below and on your knowledge
    // of science." and "{http://aristo-data.s3.amazonaws.com/images/NYSEDREGENTS/2005_4th_Grade/01.PNG}".
    diagramMatches.forEach(function(match) {
      let urlMatch = match[0];
      let preUrl = formattedQuestion.substring(index, match.index);
      // Get just the URL with curly braces.
      let url = urlMatch.substring(1, urlMatch.length - 1);
      fragments.push({preUrl:preUrl, url:url});
      index = urlMatch.length + match.index;
    });

    // Push the remainder of the string (whatever was not munched above). In our e.g., this would be
    // the part: "If the population of snakes increases, the population of frogs will most likely
    // (A) decrease (B) increase (C) remain the same".
    fragments.push({preUrl:formattedQuestion.substring(index, formattedQuestion.length)});

    // Further split the above string into the phrase preceding the answer choices and each of the
    // answer choices. These fields will be 'text' and 'answers'.
    // In the above e.g., 'text' would be: "If the population of snakes increases, the population of frog
    // will most likely" and 'answers' would be an array containing "(A) decrease",
    // "(B) increase",  "(C) remain the same".
    fragments.forEach(function(fragment) {
      let parts = fragment.preUrl.split(regExpAnswers).filter(function(s) {
        return s.trim().length > 0;
      });
      if (parts && parts.length > 0 && (parts.length - 1) % 2 === 0) {
        const text = parts.shift();
        let answers = [];
        for(var i = 0; i < parts.length; i+= 2) {
          answers.push({ label: parts[i], text: parts[i+1] });
        }
        fragment.extractedQuestion = {
          text: text,
          answers: answers,
        };
      }
    });
    return fragments;
  },

  questionTextForRenderOnHomepage(inputQuestion) {
    return inputQuestion.replace(regExpDiagram, " ");
  },

  /**
   * Returns true for Diagram question, false for text-only question.
   */
  isDiagramQuestion(inputQuestion) {
    return (inputQuestion.indexOf("{http") > -1);
  },

  /**
   * Returns true for Multiple Choice questions containing "(A)".
   * TODO: Make more robust
   */
  isMultipleChoiceQuestion(inputQuestion) {
    return (inputQuestion.indexOf("(A)") > -1 || inputQuestion.indexOf("(1)") > -1);
  },

  /** Method that renders a given question fragment appropriately,
    * for e.g., the question without choices, diagram and each answer choice.
    * Takes the following parameters:
    * questionFragment: part of a question text to render- fragments
    * will correspond to just question text (without answer choices),
    * diagram (for diagram questions) and the answer choices.
    * ix: index of the questionFragment passed here. Used to key html
    * elements corresponding to each fragment.
    * showAnswerChoices: boolean flag- true when we want to render answer
    * choices (true for QuestionCard), and false otherwise (false for
    * AnswerCard where we repeat the question with the diagram but not
    * the answer choices).
    */
  renderQuestionFragment(questionFragment, ix, showAnswerChoices) {
    let extractedQuestionText = null;
    if (questionFragment.extractedQuestion.text) {
      extractedQuestionText =
        (<span className="extracted-question">
           {questionFragment.extractedQuestion.text}
         </span>);
    }
    let extractedQuestionAnswers = null;
    if (showAnswerChoices && questionFragment.extractedQuestion.answers) {
      const questionAnswers =
         questionFragment.extractedQuestion.answers.map((answer, i) =>
           (<li key={i}>
              <span className="extracted-question-answer-label">
                {answer.label}
              </span>
              <span>
                {answer.text}
              </span>
            </li>
           ));
      extractedQuestionAnswers =
        (<ul className="extracted-question-answers">
           {questionAnswers}
         </ul>);
    }
    let image = null;
    if (questionFragment.url) {
      image =
        (<div>
           <img className="diagram" src={questionFragment.url}/>
         </div>);
    }

    return (
      <span className="asked-question-content" key={ix}>
        {extractedQuestionText}
        {extractedQuestionAnswers}
        {image}
      </span>
    );
  },

  /** Method that gets fragments using QuestionUtil for a given question, and renders them
    * using renderQuestionFragment.
    * Used by both QuestionCard and AnswerCard which repeats the question.
    */
  renderQuestion(question, showAnswerChoices) {
    const questionFragments = QuestionUtil.questionFragments(question);
    return(questionFragments.map((item, ix) => QuestionUtil.renderQuestionFragment(item, ix, showAnswerChoices)));
  },
}

export default QuestionUtil;
